/**
 * Wrapper around the native StatusBarPlugin (Capacitor Android only).
 * On web / Electron these are no-ops that read/write the preference only.
 */

const STORAGE_KEY = 'steadily_status_bar_visible';

const getPlugin = () => {
  if (typeof window === 'undefined') return null;
  const Cap = window.Capacitor;
  if (!Cap || !Cap.isNativePlatform || !Cap.isNativePlatform()) return null;
  return Cap.Plugins && Cap.Plugins.StatusBar ? Cap.Plugins.StatusBar : null;
};

export const isSupported = () => getPlugin() !== null;

export const getStoredVisibility = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === null ? true : v === 'true';
  } catch {
    return true;
  }
};

const setStoredVisibility = (visible) => {
  try {
    localStorage.setItem(STORAGE_KEY, visible ? 'true' : 'false');
  } catch {}
};

// Android WebView doesn't reliably expose system-bar insets via CSS
// env(safe-area-inset-*), so we read them from the native plugin and push
// them onto CSS custom properties (--sat, --sab, --sal, --sar). #root's
// padding references these vars.
const applyInsets = async () => {
  const plugin = getPlugin();
  if (!plugin || !plugin.getInsets) return;
  try {
    const { top = 0, bottom = 0, left = 0, right = 0 } = await plugin.getInsets();
    const root = document.documentElement;
    root.style.setProperty('--sat', top + 'px');
    root.style.setProperty('--sab', bottom + 'px');
    root.style.setProperty('--sal', left + 'px');
    root.style.setProperty('--sar', right + 'px');
    // Dispatch a resize so LayoutContext picks up the new effective size.
    window.dispatchEvent(new Event('resize'));
  } catch {}
};

export const setVisible = async (visible) => {
  setStoredVisibility(visible);
  const plugin = getPlugin();
  if (!plugin) return;
  if (visible) {
    await plugin.show();
  } else {
    await plugin.hide();
  }
  // Insets change when bars hide/show — re-read and re-apply.
  // Small delay so the WindowInsetsController has time to update.
  await new Promise((r) => setTimeout(r, 50));
  await applyInsets();
};

// Apply the saved preference. Call once at app start so the user's choice
// survives a relaunch — native plugins reset to the default (shown) otherwise.
export const applyStoredVisibility = async () => {
  const plugin = getPlugin();
  if (!plugin) return;
  const visible = getStoredVisibility();
  try {
    if (visible) await plugin.show();
    else await plugin.hide();
  } catch {}
  await new Promise((r) => setTimeout(r, 50));
  await applyInsets();
};
